'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { type ControlBarLoginAction, ControlBarLikeAction, ControlBarMoreAction, ControlBarReportAction, ControlBarSaveAction } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarActions';
import ControlBarActionTooltip from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarActionTooltip';
import ControlBarEmbedLogoButton from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarEmbedLogoButton';
import type { ControlBarViewportLayoutProps } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarViewportLayout';
import FullscreenButton from '@/app/[locale]/(worlds)/[roomId]/components/FullscreenButton';
import SharePopoverButton from '@/app/[locale]/(worlds)/[roomId]/components/SharePopoverButton';
import IconHide from '@/components/assets/icon_control_bar_hide.svg';
import IconViverseLogo from '@/components/assets/icon_control_bar_viverse.svg';
import ViverseButton from '@/components/ViverseButton';
import { type DesktopControlBarSurface, type DesktopControlBarVariant } from '@/constants/control-bar';
import { useAuthContext } from '@/context/AuthContext';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useIsEmbeddedControlBar } from '@/context/EmbeddedControlBarContext';
import { cn } from '@/lib/utils';
import type { RecommendRoom } from '@/services/cms';
export type ControlBarDesktopProps = {
  moreRecommendationsPromise?: Promise<RecommendRoom[]> | null;
  onHideAction?: () => void;
  popupContainer?: HTMLDivElement | null;
  surface?: DesktopControlBarSurface;
  variant?: DesktopControlBarVariant;
};
type ControlBarDesktopViewportOptions = {
  isHidden?: boolean;
  moreRecommendationsPromise?: Promise<RecommendRoom[]> | null;
  onHideAction?: () => void;
  surface?: DesktopControlBarSurface;
  variant?: DesktopControlBarVariant;
};
export const CONTROL_BAR_ACTION_CLASS = 'text-vive-primary-20 h-8 min-w-max shrink-0 rounded-full bg-white/15 px-4 py-2 backdrop-blur-[50px] hover:bg-white/20 hover:text-white active:bg-white/15 [&&_svg]:max-h-none @[954px]:h-[34px]';
export function DesktopControlBar(t0) {
  const $ = _c(66);
  const {
    moreRecommendationsPromise,
    onHideAction,
    popupContainer,
    surface: t1,
    variant: t2
  } = t0;
  const surface = t1 === undefined ? "normal" : t1;
  const variant = t2 === undefined ? "default" : t2;
  const t = useTranslations();
  const embedded = useIsEmbeddedControlBar();
  const {
    handleLogin
  } = useAuthContext();
  const {
    roomData
  } = useCheckEnterRoomContext();
  const [loginAction, setLoginAction] = useState(null);
  const isProVariant = variant === "viverse-pro";
  const contentKind = roomData.content_type?.code === "games" ? "games" : "experiences";
  let t3;
  if ($[0] !== handleLogin) {
    t3 = () => {
      handleLogin({
        callback: window.location.href
      });
      setLoginAction(null);
    };
    $[0] = handleLogin;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleLoginAction = t3;
  const t4 = surface === "fullscreen" && "@[705px]:basis-[200px] @[801px]:basis-[206px] @[971px]:basis-[418.5px]";
  let t5;
  if ($[2] !== t4) {
    t5 = cn("flex min-w-0 flex-1 items-center gap-2", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== embedded || $[5] !== isProVariant || $[6] !== popupContainer || $[7] !== roomData.title) {
    t6 = embedded ? !isProVariant && <ControlBarEmbedLogoButton popupContainer={popupContainer} variant="play-on" /> : <>{!isProVariant && <span className="relative h-4 w-[17px] shrink-0 @[801px]:h-[22px] @[801px]:w-6"><IconViverseLogo className="absolute top-0 left-0 h-[13px] w-4 @[801px]:h-[19.693px] @[801px]:w-[23.597px]" aria-hidden="true" /></span>}<span className="truncate text-sm leading-[22px] font-semibold @[801px]:text-base @[801px]:leading-6">{roomData.title}</span></>;
    $[4] = embedded;
    $[5] = isProVariant;
    $[6] = popupContainer;
    $[7] = roomData.title;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onHideAction || $[13] !== surface || $[14] !== t) {
    t8 = surface === "fullscreen" && <ViverseButton data-action="hide-control-bar" variant="custom" size="custom" className="bg-vive-primary-70 text-vive-primary-20 hover:bg-vive-primary-60 flex h-[34px] shrink-0 items-center gap-2 rounded-full px-4 text-sm leading-[22px] font-semibold backdrop-blur-[100px] hover:text-white" onClick={onHideAction}><IconHide className="size-5 shrink-0" aria-hidden="true" />{t("control-bar.hide-this-bar")}</ViverseButton>;
    $[12] = onHideAction;
    $[13] = surface;
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = surface === "fullscreen" && "@[705px]:grow";
  let t10;
  if ($[16] !== t9) {
    t10 = cn("flex shrink-0 items-center justify-end", t9);
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== contentKind || $[19] !== isProVariant || $[20] !== moreRecommendationsPromise || $[21] !== popupContainer) {
    t11 = !isProVariant && <ControlBarMoreAction contentKind={contentKind} moreRecommendationsPromise={moreRecommendationsPromise} popupContainer={popupContainer} />;
    $[18] = contentKind;
    $[19] = isProVariant;
    $[20] = moreRecommendationsPromise;
    $[21] = popupContainer;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== embedded || $[24] !== handleLoginAction || $[25] !== loginAction || $[26] !== popupContainer) {
    t12 = !embedded && <ControlBarLikeAction className={CONTROL_BAR_ACTION_CLASS} isLoginPopoverOpen={loginAction === "like"} onLogin={handleLoginAction} onLoginPopoverOpenChange={open => {
      if (!open && loginAction === "like") {
        setLoginAction(null);
      }
    }} onSignedOutClick={() => setLoginAction(_temp)} popupContainer={popupContainer} />;
    $[23] = embedded;
    $[24] = handleLoginAction;
    $[25] = loginAction;
    $[26] = popupContainer;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t) {
    t13 = t("action-list.share");
    $[28] = t;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== popupContainer) {
    t14 = <SharePopoverButton className={CONTROL_BAR_ACTION_CLASS} iconClassName="size-4 @[954px]:size-5" portalContainer={popupContainer} size="custom" useControlBarDialog={true} />;
    $[30] = popupContainer;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== popupContainer || $[33] !== t13 || $[34] !== t14) {
    t15 = <ControlBarActionTooltip label={t13} portalContainer={popupContainer}>{t14}</ControlBarActionTooltip>;
    $[32] = popupContainer;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== embedded || $[37] !== handleLoginAction || $[38] !== loginAction || $[39] !== popupContainer || $[40] !== surface) {
    t16 = !embedded && <><ControlBarSaveAction className={CONTROL_BAR_ACTION_CLASS} isLoginPopoverOpen={loginAction === "save"} onLogin={handleLoginAction} onLoginPopoverOpenChange={open_0 => {
        if (!open_0 && loginAction === "save") {
          setLoginAction(null);
        }
      }} onSignedOutClick={() => setLoginAction(_temp2)} popupContainer={popupContainer} /><ControlBarReportAction className={CONTROL_BAR_ACTION_CLASS} popupContainer={popupContainer} reportModalContainer={surface === "fullscreen" ? popupContainer : undefined} /></>;
    $[36] = embedded;
    $[37] = handleLoginAction;
    $[38] = loginAction;
    $[39] = popupContainer;
    $[40] = surface;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const t17 = surface === "fullscreen" ? "control-bar.exit-fullscreen" : "control-bar.fullscreen";
  let t18;
  if ($[42] !== t || $[43] !== t17) {
    t18 = t(t17);
    $[42] = t;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const t19 = surface === "fullscreen" ? "control-bar.exit-fullscreen" : "control-bar.fullscreen";
  let t20;
  if ($[45] !== t || $[46] !== t19) {
    t20 = t(t19);
    $[45] = t;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t20) {
    t21 = <span className="hidden text-sm leading-[22px] font-semibold @[954px]:inline">{t20}</span>;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t18 || $[51] !== t21) {
    t22 = <FullscreenButton ariaLabel={t18} className={CONTROL_BAR_ACTION_CLASS} iconClassName="size-4 @[954px]:size-5" label={t21} />;
    $[50] = t18;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t12 || $[54] !== t15 || $[55] !== t16 || $[56] !== t22) {
    t23 = <div className="ml-2 flex items-center gap-1 rounded-3xl backdrop-blur-xl">{t12}{t15}{t16}{t22}</div>;
    $[53] = t12;
    $[54] = t15;
    $[55] = t16;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t10 || $[59] !== t11 || $[60] !== t23) {
    t24 = <div className={t10}>{t11}{t23}</div>;
    $[58] = t10;
    $[59] = t11;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t24 || $[63] !== t7 || $[64] !== t8) {
    t25 = <div data-component="control-bar" className="bg-control-bar-surface @container relative flex min-h-12 w-full flex-nowrap items-center justify-between gap-2 rounded-b-lg p-2 text-white">{t7}{t8}{t24}</div>;
    $[62] = t24;
    $[63] = t7;
    $[64] = t8;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  return t25;
}
function _temp2(currentAction_0) {
  return currentAction_0 === "save" ? null : "save";
}
function _temp(currentAction) {
  return currentAction === "like" ? null : "like";
}
export function createDesktopControlBarViewportLayout({
  isHidden,
  moreRecommendationsPromise,
  onHideAction,
  surface = 'normal',
  variant = 'default'
}: ControlBarDesktopViewportOptions): Omit<ControlBarViewportLayoutProps, 'children'> {
  return {
    barPlacement: 'after',
    className: 'flex h-full min-h-0 min-w-0 flex-1 flex-col',
    dataComponent: 'control-bar-layout',
    dataControlBarHidden: isHidden,
    dataSurface: surface,
    renderControlBar: popupContainer => surface !== 'fullscreen' || !isHidden ? <DesktopControlBar moreRecommendationsPromise={moreRecommendationsPromise} onHideAction={onHideAction} popupContainer={popupContainer} surface={surface} variant={variant} /> : null,
    viewportDataComponent: 'control-bar-viewport'
  };
}